import { useState, useCallback } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import type { CheckDefinition } from '@internal/backstage-plugin-pulse-common';
import {
  invalidateCache,
  invalidateResultCaches,
  PULSE_CACHE_KEYS,
} from './cache';

const errorFromResponse = async (
  response: Response,
  fallback: string,
): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
};

/** Updates an existing UI-managed Pulse check definition. */
export const useUpdateCheck = () => {
  const { fetch } = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(
    async (
      checkId: string,
      check: CheckDefinition,
    ): Promise<CheckDefinition> => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `plugin://pulse/checks/${encodeURIComponent(checkId)}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(check),
          },
        );
        if (!response.ok) {
          throw await errorFromResponse(
            response,
            `Failed to update check: ${response.status}`,
          );
        }
        const updated = await response.json();
        invalidateCache(PULSE_CACHE_KEYS.checks);
        invalidateResultCaches(checkId);
        return updated;
      } catch (error_) {
        const err = error_ as Error;
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetch],
  );

  return { update, loading, error };
};
